import { useEffect, useState } from 'react';
import { TRACK_PROFILES, hav } from './track';
import { gnssState, gnssLive, getGnssCfg } from './gnss';
import { getScreenMode } from './screenMode';

// live Follow-Me recorder (global; survives re-renders, crash-safe in localStorage)
const KEY = 'giss_track_rec';
let rec = (() => { try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; } })();
let watchId = null, lock = null;
// app was closed while recording: no GPS watch runs any more -> pause, the sheet asks to resume or finish
export const interrupted = rec?.status === 'rec';
if (interrupted) { rec = { ...rec, status: 'pause', pauseAt: rec.pts?.[rec.pts.length - 1]?.t || Date.now() }; try { localStorage.setItem(KEY, JSON.stringify(rec)); } catch { /* quota */ } }
// tour left unfinished for over 6 h (app closed, other day): finish it so the sheet asks save / discard
const lastAct = rec ? Math.max(rec.pts?.[rec.pts.length - 1]?.t || 0, rec.pauseAt || 0, rec.start || 0) : 0;
export const staleDone = rec?.status === 'pause' && Date.now() - lastAct > 6 * 36e5;
if (staleDone) { rec = { ...rec, status: 'done', end: rec.pauseAt || lastAct, pauseAt: null }; try { localStorage.setItem(KEY, JSON.stringify(rec)); } catch { /* quota */ } }
const subs = new Set();
const save = () => { try { rec ? localStorage.setItem(KEY, JSON.stringify(rec)) : localStorage.removeItem(KEY); } catch { /* quota */ } };
const emit = (p) => { rec = p === null ? null : { ...rec, ...p }; save(); subs.forEach((f) => f(rec)); };

async function wake(on) {
  try {
    if (on && getScreenMode() === 'off') on = false;
    if (!on && lock) { await lock.release(); lock = null; return; }
    if (on && !lock && navigator.wakeLock) { lock = await navigator.wakeLock.request('screen'); lock.addEventListener('release', () => { lock = null; }); }
    if (!on && lock) { await lock.release(); lock = null; }
  } catch { /* not allowed */ }
}
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible' && rec?.status === 'rec') wake(true); });
window.addEventListener('giss-screen-mode', () => { if (rec?.status === 'rec') wake(true); });

function onPos(p) {
  if (!rec || (rec.status !== 'rec' && rec.status !== 'pause')) return;
  const g = gnssState();
  const gl = gnssLive(g);
  const acc = gl ? (g.hrms ?? 1) : p.coords.accuracy;
  if (acc > 35) return emit({ acc });
  const pt = gl
    ? { lat: g.lat, lon: g.lon, alt: g.alt != null ? +(g.alt - (+getGnssCfg().antenna || 0)).toFixed(2) : null, t: Date.now(), acc: +(+acc).toFixed(3), fix: g.mode === 'system' ? 'gps' : g.fix }
    : { lat: p.coords.latitude, lon: p.coords.longitude, alt: p.coords.altitude != null ? +p.coords.altitude.toFixed(1) : null, t: Date.now(), acc: Math.round(acc), fix: 'gps' };
  const m = rec.meta || {}, P = TRACK_PROFILES[rec.profile], dep = parseFloat(String(m.depth ?? '').replace(',', '.'));
  if (pt.alt != null && Number.isFinite(dep)) pt.inv = +(pt.alt - dep).toFixed(2);
  if (rec.status === 'pause') return emit({ acc, cur: { ...pt, acc } });
  const last = rec.pts[rec.pts.length - 1];
  const v = last && !rec.brk && pt.t > last.t ? (hav(last, pt) / ((pt.t - last.t) / 1000)) * 3.6 : (p.coords.speed != null ? p.coords.speed * 3.6 : null);
  const max = parseFloat(String(m.maxKmh ?? '').replace(',', '.')) || P.maxKmh, ov = !!(max && v != null && v > max);
  const live = { acc, v, ov, over: (rec.over || 0) + (ov && !rec.ov ? 1 : 0), cur: { ...pt, acc } };
  if (last && !rec.brk && hav(last, pt) < (+m.step || P.minStep)) return emit(live);
  emit({ ...live, pts: [...rec.pts, { ...pt, ...(v != null ? { v: +v.toFixed(1) } : {}), ...(rec.brk ? { brk: true } : {}) }], brk: false });
}

const watch = (on) => {
  if (on && watchId == null && navigator.geolocation) watchId = navigator.geolocation.watchPosition(onPos, () => emit({ gpsErr: true }), { enableHighAccuracy: true, maximumAge: 1000, timeout: 30000 });
  if (!on && watchId != null) { navigator.geolocation.clearWatch(watchId); watchId = null; }
  wake(on);
};
if (rec?.status === 'rec' || rec?.status === 'pause') watch(true);

export const trackRec = () => rec;
export const startTrack = (profile, no = 1, ref = null, meta = null) => { rec = { profile, no, ref, meta, over: 0, pts: [], wps: [], start: Date.now(), paused: 0, status: 'rec', brk: false }; emit({}); watch(true); };
export const pauseTrack = () => emit({ status: 'pause', pauseAt: Date.now() });
export const resumeTrack = () => { emit({ status: 'rec', paused: rec.paused + (Date.now() - rec.pauseAt), pauseAt: null, brk: true }); watch(true); };
export const stopTrack = () => {
  const now = Date.now();
  emit({ status: 'done', end: now, paused: rec.status === 'pause' ? rec.paused + (now - rec.pauseAt) : rec.paused, pauseAt: null });
  watch(false);
};
export const clearTrack = () => emit(null);
// current position for a waypoint: latest accepted fix (also while paused: last point)
export const curPos = () => rec?.cur || rec?.pts?.[rec.pts.length - 1] || null;
export const addWaypoint = (wp) => emit({ wps: [...(rec.wps || []), { ...wp, no: (rec.wps || []).length + 1 }] });
export const openTrack = () => window.dispatchEvent(new Event('giss-track-open'));

export function useTrackRec() {
  const [r, setR] = useState(rec);
  useEffect(() => { subs.add(setR); return () => subs.delete(setR); }, []);
  return r;
}
